<template>
  <div class="flex flex-col gap-y-8">
    <h3 class="text-3xl font-bold leading-6 text-color-1">
      {{ $t('InvoicesTable | create') }}
    </h3>
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <div
          v-if="
            brandingCustomization?.is_active && brandingCustomization?.logo_url
          "
          class="flex-shrink-0"
        >
          <img
            :src="brandingCustomization.logo_url"
            alt="Logo"
            class="w-[55px] h-[55px] object-cover rounded-[12px]"
          />
        </div>
        <div class="flex flex-col gap-y-1">
          <div class="text-base font-medium leading-[150%] text-color-1">
            {{ brandingTitle }}
          </div>
          <div class="flex items-center gap-2">
            <div class="text-sm font-medium leading-[150%] text-color-4">
              {{ brandingDescription }}
            </div>
            <div
              v-if="brandingCustomization?.is_active"
              class="text-sm font-medium leading-[150%] text-brand cursor-pointer hover:opacity-80 transition-opacity"
              @click="openBrandingModal"
            >
              {{ $t('Invoices | branding | edit') }}
            </div>
          </div>
        </div>
      </div>
      <base-toggle v-model="brandingEnabled" />
    </div>

    <div class="flex flex-col gap-y-3">
      <div class="text-level-7 text-color-1 font-medium">
        {{ $t('Invoices | paymentPeriod') }}
      </div>

      <div class="grid gap-3 grid-cols-2 md:grid-cols-4">
        <base-button
          v-for="button in paymentPeriods"
          :key="button.id"
          class="!w-full border !border-color-35 rounded-2.5 !p-[12px]"
          type="monochrome"
          style="max-height: 48px"
          :outline="button.value !== formData.paymentPeriod"
          @onClick="onChangePaymentPeriod(button.value)"
        >
          {{ $t(button.label) }}
        </base-button>
      </div>

      <div
        v-if="formData.paymentPeriod === 'custom'"
        class="mt-3 max-w-[245px] relative"
      >
        <date-picker v-model="formData.customDate" />
      </div>
    </div>

    <div class="flex flex-col gap-y-[16px] font-medium">
      <div class="text-level-4 text-color-1">
        {{ $t('Invoices | clientInfo') }}
      </div>
      <div class="flex flex-col gap-y-[20px]">
        <base-input
          v-model="formData.clientName"
          :label="$t('Invoices | clientName')"
          :placeholder="$t('Invoices | clientNamePlaceholder')"
        />

        <base-input
          v-model="formData.clientEmail"
          type="email"
          :label="$t('Invoices | clientEmail')"
          :placeholder="$t('Invoices | clientEmailPlaceholder')"
        />
      </div>
    </div>

    <div class="flex flex-col gap-y-[16px]">
      <div class="text-level-4 text-color-1 font-medium">
        {{ $t('Invoices | details') }}
      </div>

      <base-input
        v-model="formData.description"
        :label="$t('Invoices | description')"
        :placeholder="$t('Invoices | descriptionPlaceholder')"
      />

      <amount-input
        v-model="formData.amount"
        :label="$t('Invoices | amount')"
        :placeholder="'1000.00'"
        :currency="formData.currency"
        :max-amount="100000"
      />
    </div>

    <div class="flex flex-col gap-y-4 mt-[8px]">
      <div
        v-if="brandingCustomization?.is_active"
        class="flex items-center gap-x-4"
      >
        <span class="text-base text-color-4 font-medium whitespace-nowrap">
          {{ $t('Invoices | branding | issuer') }}
        </span>
        <div class="flex-1 h-0.5 border-t border-dashed border-color-35"></div>
        <div class="flex items-center gap-2">
          <span
            class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
          >
            {{ brandingCustomization.company_name }}
          </span>
          <svg-icon
            name="ic_edit_change"
            class="!w-6 !h-6 text-brand cursor-pointer hover:opacity-80 transition-opacity"
            @click="openBrandingModal"
          />
        </div>
      </div>

      <div class="flex items-center gap-x-4">
        <span class="text-base text-color-4 font-medium whitespace-nowrap">
          {{ $t('Invoices | paymentPeriod') }}
        </span>
        <div class="flex-1 h-0.5 border-t border-dashed border-color-35"></div>
        <span
          class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
        >
          {{ paymentPeriodText }} • {{ $t('Invoices | From') }}
          {{ formatPaymentDueDate() }}
        </span>
      </div>

      <div class="flex items-center gap-x-4">
        <span class="text-base text-color-4 font-medium whitespace-nowrap">
          {{ $t('Invoices | partnerFee | merchant') }}
        </span>
        <div class="flex-1 h-0.5 border-t border-dashed border-color-35"></div>
        <span
          class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
        >
          {{ lowerFee ? `${lowerFee * 100}%` : '0%' }}
        </span>
      </div>

      <div class="flex items-center gap-x-4">
        <span class="text-base text-color-4 font-medium whitespace-nowrap">
          {{ $t('Invoices | partnerFee | buyer') }}
        </span>
        <div class="flex-1 h-0.5 border-t border-dashed border-color-35"></div>
        <span
          class="text-base text-color-1 font-medium md:whitespace-nowrap text-right md:text-left"
        >
          {{ upperFee ? `${upperFee * 100}%` : '0%' }}
        </span>
      </div>
    </div>

    <base-button :disabled="disableButton" @onClick="onSubmit">
      <base-loader v-if="loading" :stroke-width="3" />
      <div v-else>
        {{ $t('InvoicesTable | create') }}
      </div>
    </base-button>

    <branding-modal :visible="showBrandingModal" @close="closeBrandingModal" />
  </div>
</template>

<script setup>
import { reactive, computed, watch, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import BaseInput from '@emcd-shared/base-input'
import BaseToggle from '@emcd-shared/base-toggle'
import BrandingModal from './BrandingModal.vue'
import DatePicker from '@/shared/ui/DatePicker/ui/index.vue'
import AmountInput from '@/shared/ui/AmountInput'
import SvgIcon from '@/shared/ui/SvgIcon/SvgIcon.vue'
import BaseButton from '@/shared/ui/BaseButton/BaseButton.vue'

import { APP_GETTERS } from '@/shared/constants/store-constants/app'
import { usePartnerFees } from '@/shared/composables/usePartnerFees'
import invoicesService from '@/shared/services/invoices.service'
import { generateDuplicateTitle } from '@/shared/lib/utils/generateDuplicateTitle'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  prefillData: {
    type: Object,
    default: null,
  },
})

const emits = defineEmits(['onSubmit'])

const store = useStore()
const { t } = useI18n()
const { locale } = useI18n()

const { lowerFee, upperFee, fetchPartnerSettings } = usePartnerFees()

const brandingEnabled = ref(false)
const showBrandingModal = ref(false)
const brandingCustomization = ref(null)
const isCustomizationLoaded = ref(false)
let brandingTimer = null

const paymentPeriods = [
  {
    id: 1,
    label: 'Invoices | paymentPeriods | 7days',
    value: '7days',
  },
  {
    id: 2,
    label: 'Invoices | paymentPeriods | 14days',
    value: '14days',
  },
  {
    id: 3,
    label: 'Invoices | paymentPeriods | 30days',
    value: '30days',
  },
  {
    id: 4,
    label: 'Invoices | paymentPeriods | custom',
    value: 'custom',
  },
]

const formData = reactive({
  paymentPeriod: props.prefillData?.paymentPeriod || '7days',
  customDate: props.prefillData?.customDate || null,
  clientName: props.prefillData?.clientName || '',
  clientEmail: props.prefillData?.clientEmail || '',
  description: props.prefillData
    ? generateDuplicateTitle(props.prefillData.description)
    : '',
  amount: props.prefillData?.amount || 1,
  currency: props.prefillData?.currency || 'usdt',
})

watch(
  () => props.prefillData,
  (newData) => {
    if (newData) {
      formData.paymentPeriod = newData.paymentPeriod || '7days'
      formData.customDate = newData.customDate || null
      formData.clientName = newData.clientName || ''
      formData.clientEmail = newData.clientEmail || ''
      formData.description = generateDuplicateTitle(newData.description)
      formData.amount = newData.amount || 1
      formData.currency = newData.currency || 'usdt'
    }
  },
  { immediate: true, deep: true }
)

watch(brandingEnabled, async (newValue) => {
  if (!isCustomizationLoaded.value) return

  if (newValue && !brandingCustomization.value?.is_active) {
    if (brandingTimer) {
      clearTimeout(brandingTimer)
    }
    brandingTimer = setTimeout(() => {
      showBrandingModal.value = true
    }, 3000)
  } else if (!newValue) {
    if (brandingTimer) {
      clearTimeout(brandingTimer)
      brandingTimer = null
    }
  }

  await updateBrandingStatus(newValue)
})

const loading = computed(() => store.getters[APP_GETTERS.loading])

const brandingTitle = computed(() => {
  return brandingCustomization.value?.is_active
    ? t('Invoices | branding | branded')
    : t('Invoices | branding | brandInvoice')
})

const brandingDescription = computed(() => {
  return brandingCustomization.value?.is_active
    ? t('Invoices | branding | detailsAdded')
    : t('Invoices | branding | addDetailsDescription')
})

const disableButton = computed(() => {
  const isBasicFieldsValid =
    !formData.clientName || !formData.clientEmail || !formData.amount

  const isDateValid =
    formData.paymentPeriod === 'custom'
      ? formData.customDate &&
        formData.customDate.length === 2 &&
        formData.customDate[0] &&
        formData.customDate[1]
      : true

  return isBasicFieldsValid || !isDateValid
})

function closeBrandingModal() {
  showBrandingModal.value = false
  loadBrandingCustomization()
}

function openBrandingModal() {
  showBrandingModal.value = true
}

async function loadBrandingCustomization() {
  try {
    const response = await invoicesService.getInvoiceCustomization()
    brandingCustomization.value = response.data
    brandingEnabled.value = response.data?.is_active || false
    isCustomizationLoaded.value = true
  } catch (error) {
    console.error('Failed to load branding customization:', error)
    isCustomizationLoaded.value = true
  }
}

async function updateBrandingStatus(isActive) {
  try {
    await invoicesService.upsertInvoiceCustomization({
      company_name: brandingCustomization.value?.company_name || '',
      registration_number:
        brandingCustomization.value?.registration_number || '',
      registered_address: brandingCustomization.value?.registered_address || '',
      is_active: isActive,
    })

    isCustomizationLoaded.value = false
    await loadBrandingCustomization()
  } catch (error) {
    console.error('Failed to update branding status:', error)
  }
}

const paymentPeriodText = computed(() => {
  const period = paymentPeriods.find((p) => p.value === formData.paymentPeriod)
  return period ? t(period.label) : ''
})

function onChangePaymentPeriod(value) {
  formData.paymentPeriod = value
  if (value !== 'custom') {
    formData.customDate = null
  }
}

function onSubmit() {
  const submitData = {
    ...formData,
    paymentDue: calculatePaymentDue(),
  }

  emits('onSubmit', submitData)
}

function calculatePaymentDue() {
  if (formData.paymentPeriod === 'custom') {
    return formData.customDate && formData.customDate[1]
      ? formData.customDate[1]
      : null
  }

  const date = new Date()
  switch (formData.paymentPeriod) {
    case '7days':
      date.setDate(date.getDate() + 7)
      break
    case '14days':
      date.setDate(date.getDate() + 14)
      break
    case '30days':
      date.setDate(date.getDate() + 30)
      break
  }

  return date
}

function formatPaymentDueDate() {
  const currentDate = new Date()
  const dueDate = calculatePaymentDue()

  if (!dueDate) return ''

  const optionsWithYear = {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }

  const optionsWithoutYear = {
    day: 'numeric',
    month: 'long',
  }

  const currentDateFormatted = new Intl.DateTimeFormat(
    locale.value,
    optionsWithoutYear
  ).format(currentDate)

  const dueDateFormatted = new Intl.DateTimeFormat(
    locale.value,
    optionsWithYear
  ).format(dueDate)

  if (
    formData.paymentPeriod === 'custom' &&
    formData.customDate &&
    formData.customDate.length === 2
  ) {
    const startDate = formData.customDate[0]
    const endDate = formData.customDate[1]
    const sameMonthYear =
      startDate.getMonth() === endDate.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear()

    if (sameMonthYear) {
      const startDay = startDate.getDate()
      return `${startDay} ${t('Invoices | To')} ${dueDateFormatted}`
    } else {
      const startDateFormatted = new Intl.DateTimeFormat(
        locale.value,
        optionsWithoutYear
      ).format(startDate)
      return `${startDateFormatted} ${t('Invoices | To')} ${dueDateFormatted}`
    }
  }

  const sameMonthYear =
    currentDate.getMonth() === dueDate.getMonth() &&
    currentDate.getFullYear() === dueDate.getFullYear()

  if (sameMonthYear) {
    const currentDay = currentDate.getDate()
    return `${currentDay} ${t('Invoices | To')} ${dueDateFormatted}`
  }

  return `${currentDateFormatted} ${t('Invoices | To')} ${dueDateFormatted}`
}

onMounted(async () => {
  await Promise.all([fetchPartnerSettings('usdt'), loadBrandingCustomization()])
})
</script>
